/**
 * Mantine component mapping for react-markdown table rendering.
 *
 * Provides consistent table styling when rendering markdown content.
 */

import { Divider, Table, Title } from '@mantine/core';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';

/**
 * Component mapping for react-markdown table elements.
 * Maps HTML table elements to Mantine equivalents.
 */
export const markdownTableComponents = {
  table({ children }: { children?: ReactNode }) {
    return <Table>{children}</Table>;
  },
  thead({ children }: { children?: ReactNode }) {
    return <Table.Thead>{children}</Table.Thead>;
  },
  tbody({ children }: { children?: ReactNode }) {
    return <Table.Tbody>{children}</Table.Tbody>;
  },
  tr({ children }: { children?: ReactNode }) {
    return <Table.Tr>{children}</Table.Tr>;
  },
  th({ children }: { children?: ReactNode }) {
    return <Table.Th>{children}</Table.Th>;
  },
  td({ children }: { children?: ReactNode }) {
    return <Table.Td>{children}</Table.Td>;
  },
  hr() {
    return <Divider my="md" />;
  },
  h1({ children }: { children?: ReactNode }) {
    return <Title order={1}>{children}</Title>;
  },
  h2({ children }: { children?: ReactNode }) {
    return <Title order={2}>{children}</Title>;
  },
  h3({ children }: { children?: ReactNode }) {
    return <Title order={3}>{children}</Title>;
  },
  h4({ children }: { children?: ReactNode }) {
    return <Title order={4}>{children}</Title>;
  },
  h5({ children }: { children?: ReactNode }) {
    return <Title order={5}>{children}</Title>;
  },
  h6({ children }: { children?: ReactNode }) {
    return <Title order={6}>{children}</Title>;
  },
  p({ children }: { children?: ReactNode }) {
    return <motion.p>{children}</motion.p>;
  },
  ol({ children }: { children?: ReactNode }) {
    return <motion.ol>{children}</motion.ol>;
  },
  ul({ children }: { children?: ReactNode }) {
    return <motion.ul>{children}</motion.ul>;
  },
  li({ children }: { children?: ReactNode }) {
    return <motion.li>{children}</motion.li>;
  },
};
